import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Search, Building2, Check, X, Plus, Mail, User, AlertCircle } from "lucide-react";
import CONFIG from "@/lib/config";
import { apiRequest } from "@/lib/api";

interface AddMemberToACCDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onMemberAdded?: () => void;
}

interface MemberData {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  companyId?: string;
}

export function AddMemberToACCDialog({ 
  isOpen, 
  onClose, 
  projectId, 
  onMemberAdded
}: AddMemberToACCDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Email input state
  const [emailInput, setEmailInput] = useState("");
  const [members, setMembers] = useState<MemberData[]>([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [showCompanySearch, setShowCompanySearch] = useState<{[key: number]: boolean}>({});
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
  const [currentMemberIndex, setCurrentMemberIndex] = useState<number | null>(null);
  
  // State for select all and batch edit
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(new Set());
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [batchCompany, setBatchCompany] = useState("");
  const [showBatchCompanySearch, setShowBatchCompanySearch] = useState(false);

  // Get existing companies list
  const { data: existingCompanies = [], isLoading: companiesLoading, refetch: refetchCompanies } = useQuery<Array<{id: string, name: string}>>({
    queryKey: ["acc-companies", projectId],
    queryFn: async () => {
      // Use direct fetch instead of apiRequest
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/acc/companies?projectId=${projectId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      const data = await response.json();
      return data.companies || [];
    },
    enabled: isOpen && !!projectId,
  });

  // Add email to members list
  const handleAddEmail = () => {
    if (!emailInput.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    // Parse multiple emails with different separators (; , space)
    const emailRegex = /[;,\s]+/;
    const emails = emailInput.split(emailRegex)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emails.length === 0) {
      toast({
        title: "Error",
        description: "No valid email addresses found",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailValidationRegex.test(email));
    
    if (invalidEmails.length > 0) {
      toast({
        title: "Error",
        description: `Invalid email format: ${invalidEmails.join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    // Check for duplicates (both in input and existing members)
    const duplicateEmails = emails.filter(email => 
      emails.indexOf(email) !== emails.lastIndexOf(email) || // duplicates within input
      members.some(member => member.email && member.email.toLowerCase() === email.toLowerCase()) // duplicates with existing
    );

    if (duplicateEmails.length > 0) {
      toast({
        title: "Error",
        description: `Duplicate emails found: ${duplicateEmails.join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    // Add all valid emails as new members
    const newMembers: MemberData[] = emails.map(email => ({
      email: email,
      firstName: "",
      lastName: "",
      company: ""
    }));

    setMembers(prev => [...prev, ...newMembers]);
    setEmailInput("");
    
    toast({
      title: "Success",
      description: `Added ${emails.length} email${emails.length > 1 ? 's' : ''} successfully`,
    });
  };

  // Update member information
  const updateMember = (index: number, field: keyof MemberData, value: string) => {
    setMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));
  };

  // Remove member
  const removeMember = (index: number) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  // Handle company selection
  const handleCompanySelect = (companyName: string) => {
    setSelectedCompany(companyName);
    setNewCompanyName("");
    setShowCompanySearch(false);
  };

  // Handle create new company
  const handleCreateCompany = () => {
    if (!newCompanyName.trim()) {
      toast({
        title: "Error",
        description: "Please enter company name",
        variant: "destructive",
      });
      return;
    }
    createCompanyMutation.mutate(newCompanyName.trim());
  };

  // Handle company input change, check if need to show add prompt
  const handleCompanyInputChange = (index: number, value: string) => {
    updateMember(index, 'company', value);
    
    // If entered company name is not in existing company list, show add prompt
    if (value.trim() && !existingCompanies.some(company => 
      company.name && company.name.toLowerCase() === value.toLowerCase()
    )) {
      setShowCompanySearch(prev => ({ ...prev, [index]: true }));
    }
  };

  // Select all functionality
  const handleSelectAll = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(members.map((_, index) => index)));
    }
  };

  // Toggle individual member selection
  const toggleMemberSelection = (index: number) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedMembers(newSelected);
  };

  // Batch edit company
  const handleBatchEditCompany = () => {
    if (batchCompany && selectedMembers.size > 0) {
      const updatedMembers = members.map((member, index) => {
        if (selectedMembers.has(index)) {
          return { ...member, company: batchCompany };
        }
        return member;
      });
      setMembers(updatedMembers);
      setBatchCompany("");
      setSelectedMembers(new Set());
      setShowBatchEdit(false);
      setShowBatchCompanySearch(false);
      
      toast({
        title: "Success",
        description: `Updated company for ${selectedMembers.size} members`,
      });
    }
  };

  // Handle batch company input change
  const handleBatchCompanyInputChange = (value: string) => {
    setBatchCompany(value);
    
    // If entered company name is not in existing company list, show add prompt
    if (value.trim() && !existingCompanies.some(company => 
      company.name && company.name.toLowerCase() === value.toLowerCase()
    )) {
      setShowBatchCompanySearch(true);
    }
  };

  // Filter companies for batch edit
  const filteredBatchCompanies = () => {
    if (!batchCompany.trim()) return existingCompanies;
    return existingCompanies.filter(company => 
      company.name && company.name.toLowerCase().includes(batchCompany.toLowerCase())
    );
  };

  // Handle batch company selection
  const handleBatchCompanySelection = (companyName: string) => {
    setBatchCompany(companyName);
    setShowBatchCompanySearch(false);
  };

  // Handle batch create new company
  const handleBatchCreateNewCompany = () => {
    if (batchCompany.trim()) {
      // Create company and add to account
      createCompanyMutation.mutate(batchCompany.trim());
      setShowBatchCompanySearch(false);
    }
  };

  // Batch remove members
  const handleBatchRemove = () => {
    if (selectedMembers.size > 0) {
      const updatedMembers = members.filter((_, index) => !selectedMembers.has(index));
      setMembers(updatedMembers);
      setSelectedMembers(new Set());
      
      toast({
        title: "Success",
        description: `Removed ${selectedMembers.size} members`,
      });
    }
  };

  // Open add company dialog
  const openAddCompanyDialog = (memberIndex: number) => {
    setCurrentMemberIndex(memberIndex);
    setNewCompanyName(members[memberIndex].company);
    setShowAddCompanyDialog(true);
    setShowCompanySearch(false);
  };

  // Filter company list based on current member's company input
  const filteredCompanies = (memberIndex: number) => {
    const currentMember = members[memberIndex];
    if (!currentMember?.company) return existingCompanies;
    
    return existingCompanies.filter(company =>
      company.name && company.name.toLowerCase().includes(currentMember.company.toLowerCase())
    );
  };

  // Create company mutation - 使用新的API端点，直接添加到ACC Account
  const createCompanyMutation = useMutation({
    mutationFn: async (companyName: string) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/acc/companies/create-and-add-to-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId,
          name: companyName,
          industry: "Specialties",
          country: "United Kingdom"
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create company and add to account');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Refresh company list
      refetchCompanies();
      // Close dialog
      setShowAddCompanyDialog(false);
      // If current member is selected, update their company info
      if (currentMemberIndex !== null) {
        updateMember(currentMemberIndex, 'company', data.company.name);
      }
      toast({
        title: "Success",
        description: `Company "${data.company.name}" created and added to ACC Account successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create company and add to account",
        variant: "destructive",
      });
    },
  });

  // Add members to ACC mutation
  const addMembersMutation = useMutation({
    mutationFn: async (membersData: MemberData[]) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/acc/members/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId,
          members: membersData.map(member => ({
            ...member,
            companyId: member.companyId
          }))
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add members to ACC');
      }
      return response.json();
    },
    onSuccess: () => {
      // Clear all related cache
      queryClient.invalidateQueries({ queryKey: ["project-users", projectId] });
      queryClient.invalidateQueries({ queryKey: ["acc-companies", projectId] });
      queryClient.invalidateQueries({ queryKey: ["user-access-stats"] });
      queryClient.invalidateQueries({ queryKey: ["company-stats"] });
      queryClient.invalidateQueries({ queryKey: ["activities-data"] });
      queryClient.invalidateQueries({ queryKey: ["companies-data", projectId] }); // 修复：使用正确的 queryKey
      queryClient.invalidateQueries({ queryKey: ["project-users"] });
      
      onClose();
      setMembers([]);
      setEmailInput("");
      setSelectedCompany("");
      setNewCompanyName("");
      if (onMemberAdded) {
        onMemberAdded();
      }
      toast({
        title: "Success",
        description: "Members added to ACC successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add members to ACC",
        variant: "destructive",
      });
    },
  });

  // Submit all members
  const handleSubmit = () => {
    if (members.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one member",
        variant: "destructive",
      });
      return;
    }

    // Validate all member information
    const incompleteMembers = members.filter(member => 
      !member.firstName.trim() || !member.lastName.trim() || !member.company.trim()
    );

    if (incompleteMembers.length > 0) {
      toast({
        title: "Error",
        description: "Please fill in complete information for all members",
        variant: "destructive",
      });
      return;
    }

    addMembersMutation.mutate(members);
  };

  const handleClose = () => {
    onClose();
    setMembers([]);
    setEmailInput("");
    setSelectedCompany("");
    setNewCompanyName("");
    setShowCompanySearch(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[99vw] h-[98vh] max-w-none overflow-hidden flex flex-col mx-0">
        <DialogHeader>
          <DialogTitle>Add Members to ACC</DialogTitle>
          <DialogDescription>
            Add new members to Autodesk Construction Cloud project. Maximum 100 email addresses.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col space-y-6 p-2">
          {/* Email input area */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="email" className="text-lg font-medium">
                Up to 100 email addresses*
              </Label>
              <AlertCircle className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input
                  id="email"
                  placeholder="person@company.com; person2@company.com, person3@company.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  className="h-12 text-lg"
                />
              </div>
              <Button onClick={handleAddEmail} disabled={!emailInput.trim()} className="h-12 px-8 text-lg">
                Add
              </Button>
            </div>
          </div>

          {/* Members list */}
          {members.length > 0 && (
            <div className="space-y-4 flex-1 flex flex-col">
               <div className="flex items-center justify-between">
                 <h3 className="text-xl font-semibold">Member Details</h3>
                 <div className="flex items-center space-x-4">
                   <span className="text-lg text-gray-500 font-medium">{members.length} members</span>
                   {selectedMembers.size > 0 && (
                     <div className="flex items-center space-x-2">
                       <span className="text-sm text-blue-600">{selectedMembers.size} selected</span>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => setShowBatchEdit(true)}
                         className="text-blue-600 hover:text-blue-700"
                       >
                         Batch Edit Company
                       </Button>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={handleBatchRemove}
                         className="text-red-600 hover:text-red-700"
                       >
                         Batch Remove
                       </Button>
                     </div>
                   )}
                 </div>
               </div>
              
              <div className="border rounded-lg overflow-hidden flex-1 flex flex-col">
                <div className="overflow-x-auto flex-1">
                  <table className="w-full divide-y divide-gray-200">
                     <thead className="bg-gray-50">
                       <tr>
                         <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-24">
                           <input 
                             type="checkbox" 
                             className="rounded border-gray-300"
                             checked={selectedMembers.size === members.length && members.length > 0}
                             onChange={handleSelectAll}
                           />
                         </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-48">
                          Email*
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-40">
                          First Name
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-40">
                          Last Name
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider w-80">
                          Company
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                       {members.map((member, index) => (
                         <tr key={index} className={`hover:bg-gray-50 ${selectedMembers.has(index) ? 'bg-blue-50' : ''}`}>
                           <td className="px-6 py-5 whitespace-nowrap">
                             <input 
                               type="checkbox" 
                               className="rounded border-gray-300"
                               checked={selectedMembers.has(index)}
                               onChange={() => toggleMemberSelection(index)}
                             />
                           </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{member.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <Input
                              placeholder="First name"
                              value={member.firstName}
                              onChange={(e) => updateMember(index, 'firstName', e.target.value)}
                              className="w-full min-w-40 h-10"
                            />
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <Input
                              placeholder="Last name"
                              value={member.lastName}
                              onChange={(e) => updateMember(index, 'lastName', e.target.value)}
                              className="w-full min-w-40 h-10"
                            />
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap">
                            <div className="relative">
                               <Input
                                 placeholder="Type company name or search existing companies..."
                                 value={member.company}
                                 onChange={(e) => handleCompanyInputChange(index, e.target.value)}
                                 onFocus={() => setShowCompanySearch(prev => ({ ...prev, [index]: true }))}
                                 onBlur={() => {
                                   // 延迟关闭，让用户有时间点击选项
                                   setTimeout(() => {
                                     setShowCompanySearch(prev => ({ ...prev, [index]: false }));
                                   }, 200);
                                 }}
                                 className="pr-10"
                               />
                               <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                               
                               {/* Company search dropdown */}
                               {showCompanySearch[index] && (
                                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                  <div className="p-2">
                                    <div className="max-h-40 overflow-y-auto">
                                      {filteredCompanies(index).map((company, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                           onClick={() => {
                                             updateMember(index, 'company', company.name);
                                             updateMember(index, 'companyId', company.id);
                                             setShowCompanySearch(prev => ({ ...prev, [index]: false }));
                                           }}
                                        >
                                          <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                                          <span className="text-sm">{company.name}</span>
                                        </div>
                                      ))}
                                      {filteredCompanies(index).length === 0 && member.company && (
                                        <div className="p-2 text-sm text-gray-500">
                                          No matching companies found
                                        </div>
                                      )}
                                      {filteredCompanies(index).length === 0 && !member.company && (
                                        <div className="p-2 text-sm text-gray-500">
                                          No available company list
                                        </div>
                                      )}
                                    </div>
                                    <div className="border-t pt-2 mt-2">
                                      <div className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                           onClick={() => openAddCompanyDialog(index)}>
                                        <Plus className="h-4 w-4 text-blue-500 mr-2" />
                                        <span className="text-sm text-blue-600">
                                          Add company "{member.company || "New Company"}"
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-6">
          <Button variant="outline" onClick={handleClose} className="h-12 px-8 text-lg">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={addMembersMutation.isPending || members.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-lg"
          >
            {addMembersMutation.isPending ? "Adding..." : `Add ${members.length} members to ACC`}
          </Button>
        </DialogFooter>
        </DialogContent>
        
        {/* Batch edit dialog */}
        <Dialog open={showBatchEdit} onOpenChange={setShowBatchEdit}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Batch Edit Company</DialogTitle>
              <DialogDescription>
                Set company for {selectedMembers.size} selected members.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="batchCompany" className="text-sm font-medium">
                  Company Name
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="batchCompany"
                    placeholder="Type company name or search existing companies..."
                    value={batchCompany}
                    onChange={(e) => handleBatchCompanyInputChange(e.target.value)}
                    onFocus={() => setShowBatchCompanySearch(true)}
                    onBlur={() => {
                      // 延迟关闭，让用户有时间点击选项
                      setTimeout(() => {
                        setShowBatchCompanySearch(false);
                      }, 200);
                    }}
                    className="pr-10"
                  />
                  <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  
                  {/* Company search dropdown */}
                  {showBatchCompanySearch && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                      <div className="p-2">
                        <div className="max-h-40 overflow-y-auto">
                          {filteredBatchCompanies().map((company, idx) => (
                            <div
                              key={idx}
                              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handleBatchCompanySelection(company.name)}
                            >
                              <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm">{company.name}</span>
                            </div>
                          ))}
                          {filteredBatchCompanies().length === 0 && batchCompany && (
                            <div className="p-2 text-sm text-gray-500">
                              No matching companies found
                            </div>
                          )}
                          {filteredBatchCompanies().length === 0 && !batchCompany && (
                            <div className="p-2 text-sm text-gray-500">
                              No available company list
                            </div>
                          )}
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                               onClick={handleBatchCreateNewCompany}>
                            <Plus className="h-4 w-4 text-blue-500 mr-2" />
                            <span className="text-sm text-blue-600">
                              Add company "{batchCompany || "New Company"}"
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowBatchEdit(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBatchEditCompany}
                disabled={!batchCompany.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Update Company
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Add company dialog */}
      <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Company</DialogTitle>
            <DialogDescription>
              Enter company details. Only company name is required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName" className="text-sm font-medium">
                Name *
              </Label>
              <Input
                id="companyName"
                placeholder="Enter company name"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddCompanyDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCompany}
              disabled={createCompanyMutation.isPending || !newCompanyName.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createCompanyMutation.isPending ? "Adding..." : "Add Company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}